import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Check, CheckCircle2, X, XCircle } from 'lucide-react'
import type { SaveSonarQubeConnectionRequest, SonarQubeConnection, TestSonarQubeConnectionRequest } from '@/types'
import { getErrorCode, getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'

interface FormValues {
  connectionName: string
  serverUrl: string
  projectName: string
  projectKey: string
  accessToken: string
}

interface TestFeedback {
  kind: 'success' | 'unauthorized' | 'operationFailed' | 'error'
  message: string
}

interface SonarQubeConnectionFormProps {
  connection: SonarQubeConnection | undefined
  open: boolean
  onTest: (data: TestSonarQubeConnectionRequest) => Promise<void>
  onSave: (data: SaveSonarQubeConnectionRequest) => Promise<void>
  onCancel?: () => void
  isTesting: boolean
  isSaving: boolean
  submitLabel?: string
  submitSavingLabel?: string
  tokenInlineError?: string | null
  saveError?: string | null
}

export function SonarQubeConnectionForm({
  connection,
  open,
  onTest,
  onSave,
  onCancel,
  isTesting,
  isSaving,
  submitLabel = 'Save Connection',
  submitSavingLabel = 'Saving...',
  tokenInlineError,
  saveError,
}: SonarQubeConnectionFormProps) {
  const isConnected = connection !== undefined
  const [testFeedback, setTestFeedback] = useState<TestFeedback | null>(null)
  const [saveErrorDismissed, setSaveErrorDismissed] = useState(false)
  const [lastSaveError, setLastSaveError] = useState(saveError)

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { connectionName: '', serverUrl: '', projectName: '', projectKey: '', accessToken: '' },
  })

  const isBusy = isTesting || isSaving

  if (saveError !== lastSaveError) {
    setLastSaveError(saveError)
    setSaveErrorDismissed(false)
  }
  const showSaveError = !!saveError && !saveErrorDismissed

  useEffect(() => {
    reset({
      connectionName: connection?.connectionName ?? '',
      serverUrl: connection?.serverUrl ?? '',
      projectName: connection?.projectName ?? '',
      projectKey: connection?.projectKey ?? '',
      accessToken: '',
    })
  }, [connection, open, reset])

  const clearStaleFeedback = () => {
    setTestFeedback(null)
    setSaveErrorDismissed(true)
  }

  const registerField = (name: keyof FormValues, options?: Record<string, unknown>) => {
    const field = register(name, options as never)
    return {
      ...field,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        clearStaleFeedback()
        return field.onChange(e)
      },
    }
  }

  const handleTestConnection = async () => {
    const values = getValues()
    setTestFeedback(null)
    try {
      await onTest({ serverUrl: values.serverUrl, accessToken: values.accessToken || undefined, projectKey: values.projectKey })
      setTestFeedback({ kind: 'success', message: 'Connection settings are valid.' })
    } catch (error) {
      if (getErrorCode(error) === 'CONNECTOR_UNAUTHORIZED') {
        setTestFeedback({ kind: 'unauthorized', message: getErrorMessage(error) ?? 'Invalid token — check your SonarQube token.' })
      } else if (getErrorCode(error) === 'CONNECTOR_OPERATION_FAILED') {
        setTestFeedback({ kind: 'operationFailed', message: getErrorMessage(error) ?? 'Token is valid but the project key or server could not be verified.' })
      } else {
        setTestFeedback({ kind: 'error', message: extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Could not verify the connection.' })
      }
    }
  }

  const onSubmit = async (data: FormValues) => {
    await onSave({
      connectionName: data.connectionName,
      serverUrl: data.serverUrl,
      projectName: data.projectName || undefined,
      projectKey: data.projectKey,
      accessToken: data.accessToken || undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="sonarqube-connection-name">Connection Name <span className="text-red-500">*</span></Label>
          <Input
            id="sonarqube-connection-name"
            data-testid="sonarqube-connection-name"
            type="text"
            placeholder="SonarQube - Production"
            {...registerField('connectionName', { required: 'Connection name is required' })}
          />
          {errors.connectionName && <p role="alert" className="text-sm text-destructive">{errors.connectionName.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sonarqube-server-url">Server URL <span className="text-red-500">*</span></Label>
          <Input
            id="sonarqube-server-url"
            data-testid="sonarqube-server-url"
            type="text"
            placeholder="https://sonarqube.example.com"
            {...registerField('serverUrl', { required: 'Server URL is required' })}
          />
          {errors.serverUrl && <p role="alert" className="text-sm text-destructive">{errors.serverUrl.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sonarqube-access-token">
            Access Token {!isConnected && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id="sonarqube-access-token"
            data-testid="sonarqube-access-token"
            type="password"
            autoComplete="new-password"
            placeholder={isConnected ? '••••••••••••••••' : 'Paste your access token'}
            {...registerField('accessToken', { required: isConnected ? false : 'Access token is required' })}
          />
          {tokenInlineError ? (
            <p role="alert" className="text-sm text-destructive">{tokenInlineError}</p>
          ) : errors.accessToken ? (
            <p role="alert" className="text-sm text-destructive">{errors.accessToken.message}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {isConnected && 'Leave blank to keep the current token. '}Stored securely and shared by everyone with access to this project.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sonarqube-project-name">Project Name</Label>
          <Input
            id="sonarqube-project-name"
            data-testid="sonarqube-project-name"
            type="text"
            placeholder="IDSP"
            {...registerField('projectName')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="sonarqube-project-key">Project Key <span className="text-red-500">*</span></Label>
          <Input
            id="sonarqube-project-key"
            data-testid="sonarqube-project-key"
            type="text"
            placeholder="my-org-my-project"
            {...registerField('projectKey', { required: 'Project key is required' })}
          />
          {errors.projectKey && <p role="alert" className="text-sm text-destructive">{errors.projectKey.message}</p>}
        </div>

        {testFeedback && (
          <div
            role={testFeedback.kind === 'success' ? 'status' : 'alert'}
            data-testid="sonarqube-test-feedback"
            className={`flex items-start gap-2 rounded-md p-3 text-sm ${
              testFeedback.kind === 'success' ? 'bg-success/10 text-success-foreground' : 'bg-destructive/10 text-destructive'
            }`}
          >
            {testFeedback.kind === 'success' ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
            )}
            <span className="flex-1">{testFeedback.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              data-testid="sonarqube-test-feedback-dismiss"
              onClick={() => setTestFeedback(null)}
              className="shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {showSaveError && (
          <div
            role="alert"
            data-testid="sonarqube-save-error"
            className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
          >
            <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="flex-1">{saveError}</span>
            <button
              type="button"
              aria-label="Dismiss"
              data-testid="sonarqube-save-error-dismiss"
              onClick={() => setSaveErrorDismissed(true)}
              className="shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={isBusy}>
              Cancel
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            data-testid="sonarqube-test-connection-button"
            disabled={isBusy || !isValid}
            onClick={handleTestConnection}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button
            type="submit"
            data-testid="sonarqube-save-connection-button"
            disabled={isBusy || !isValid}
          >
            {isSaving ? (
              submitSavingLabel
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                {submitLabel}
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}
